import { LoginProviderType } from '@common/services/auth/auth.service.interface';
import {
  PersistedStoreState,
  StoreConfig,
  basePersistenceStoreKey,
} from '@scoring-web/configs/store/store.config';
import { getStoreBaseActions, getStoreHydrationActions } from '@scoring-web/utils/store.util';
import { User } from '@user/models/user.model';
import { proxy, subscribe, useSnapshot } from 'valtio';

// #region Types
type UserState = PersistedStoreState & {
  loginType?: LoginProviderType;
  subjectType?: number;
  token: string;
  user?: User;
};
// #endregion

// #region State
const initial = {
  loginType: undefined,
  subjectType: undefined,
  token: '',
  user: undefined,
} satisfies UserState;
const state = proxy<UserState>({ ...initial });
// #endregion

// #region Snapshot
export function useUserStore() {
  return useSnapshot(state);
}
// #endregion

// #region Constants
const persistenceKey = `${basePersistenceStoreKey}.user`;
// #endregion

// #region Actions
const actions = {
  ...getStoreBaseActions(state, initial),
  ...getStoreHydrationActions(persistenceKey, state),
  setLoginType: (loginType?: LoginProviderType) => {
    state.loginType = loginType;
  },
  setSubjectType: (subjectType?: number) => {
    state.subjectType = subjectType;
  },
  setUser: (user?: User) => {
    state.user = user;
  },
  updateToken: (token: string) => {
    state.token = token;
  },
};
// #endregion

// #region Store Initialization
actions.hydrate();
subscribe(state, () => actions.persist());
// #endregion

// #region Store
type UserStore = StoreConfig<UserState, typeof actions>;
const userStore: UserStore = { actions, initial, state };
// #endregion

export default userStore;
